import React from 'react';
import { useNavigate } from 'react-router-dom';
import { StudentProgress } from '../types';
import { quizSections } from '../data/questions';

interface SummaryPageProps {
  progress: StudentProgress;
  onRetakeSection: (sectionIndex: number, retakeType?: 'full' | 'partial') => void;
  onDownloadCertificate: () => void;
  onReset: () => void;
  onProgressUpdate: (progress: StudentProgress) => void;
}

const SummaryPage: React.FC<SummaryPageProps> = ({
  progress,
  onRetakeSection,
  onDownloadCertificate,
  onReset,
  onProgressUpdate
}) => {
  const navigate = useNavigate();
  const overallScore = progress.sections.reduce((sum, section) => sum + section.score, 0) / progress.sections.length;
  const allSectionsPassed = progress.sections.every(section => section.passed);
  const passedSections = progress.sections.filter(section => section.passed).length;
  const isPerfectScore = Math.round(overallScore * 100) === 100;

  const handleViewCertificate = () => {
    // Mark certificate as viewed and navigate
    const updatedProgress = {
      ...progress,
      certificateViewed: true
    };
    onProgressUpdate(updatedProgress);
    navigate('/certificate');
  };

  const getSectionRetakeType = (sectionProgress: any, section: any): 'full' | 'partial' => {
    const totalQuestions = section.questions.length;
    const incorrectCount = totalQuestions - (sectionProgress.answers?.filter((a: any) => a.isCorrect).length || 0);

    // If more than 50% incorrect, retake entire section
    if (incorrectCount > totalQuestions * 0.5) return 'full';

    return 'partial';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Quiz Results Summary
          </h1>
          <p className="text-xl text-gray-600">{progress.email}</p>
        </div>

        {/* Overall Score Card */}
        <div className={`card p-8 mb-8 ${
          allSectionsPassed
            ? 'bg-green-50 border-green-200'
            : 'bg-orange-50 border-orange-200'
        }`}>
          <div className="text-center">
            <div className={`text-6xl font-bold mb-4 ${
              allSectionsPassed ? 'text-green-600' : 'text-orange-600'
            }`}>
              {allSectionsPassed ? '100' : Math.round(overallScore * 100)}%
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              {allSectionsPassed ? 'Congratulations! 🎉' : 'Almost There! 💪'}
            </h2>

            <p className="text-lg text-gray-700 mb-6">
              {allSectionsPassed
                ? 'You have successfully completed all sections of the robotics safety quiz!'
                : `You have passed ${passedSections} of ${quizSections.length} sections. Complete the remaining sections to get your certificate.`}
            </p>

            {allSectionsPassed && (
              <div className="mb-6">
                {isPerfectScore ? (
                  <button
                    onClick={handleViewCertificate}
                    className="btn-primary px-8 py-3 text-lg mr-4"
                  >
                    🏆 View Certificate
                  </button>
                ) : (
                  <button
                    onClick={onDownloadCertificate}
                    className="btn-primary px-8 py-3 text-lg mr-4"
                  >
                    📜 Download Certificate
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Section Results */}
        <div className="card p-6 mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Section Results</h3>

          <div className="space-y-4">
            {quizSections.map((section, index) => {
              const sectionProgress = progress.sections[index];
              const totalQuestions = section.questions.length;
              const correctAnswers = sectionProgress?.answers.filter(a => a.isCorrect).length || 0;
              const sectionScore = totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;
              const passed = sectionProgress?.passed || false;
              const retakeType = !passed ? getSectionRetakeType(sectionProgress, section) : 'full';

              return (
                <div
                  key={section.id}
                  className={`p-4 rounded-lg border-2 ${
                    passed
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="text-lg font-semibold text-gray-900 mr-3">
                          {section.name}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          passed
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {passed ? 'PASSED' : 'NEEDS RETAKE'}
                        </span>
                      </div>

                      <div className="text-sm text-gray-600 mb-2">
                        {correctAnswers} of {totalQuestions} questions correct ({Math.round(sectionScore)}%)
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            passed ? 'bg-green-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${sectionScore}%` }}
                        ></div>
                      </div>
                    </div>

                    {!passed && (
                      <div className="ml-4">
                        <button
                          onClick={() => onRetakeSection(index, retakeType)}
                          className="btn-secondary"
                        >
                          {retakeType === 'partial' ? 'Retake Incorrect Questions' : 'Retake Section'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="text-center space-x-4">
          {!allSectionsPassed && (
            <button
              onClick={() => {
                const firstFailedSection = progress.sections.findIndex(s => !s.passed);
                if (firstFailedSection !== -1) {
                  const failedSectionProgress = progress.sections[firstFailedSection];
                  const failedSection = quizSections[firstFailedSection];
                  const retakeType = getSectionRetakeType(failedSectionProgress, failedSection);
                  onRetakeSection(firstFailedSection, retakeType);
                }
              }}
              className="btn-primary px-6 py-3"
            >
              Continue with Failed Sections
            </button>
          )}

          <button
            onClick={onReset}
            className="btn-secondary px-6 py-3"
          >
            Start New Quiz
          </button>
        </div>

        {/* Statistics */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card p-6 text-center">
            <div className="text-3xl font-bold text-blue-600 mb-2">{passedSections}</div>
            <div className="text-sm font-medium text-gray-600">Sections Passed</div>
          </div>

          <div className="card p-6 text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">
              {progress.sections.reduce((sum, section) =>
                sum + (section.answers?.filter(a => a.isCorrect).length || 0), 0
              )}
            </div>
            <div className="text-sm font-medium text-gray-600">Correct Answers</div>
          </div>

          <div className="card p-6 text-center">
            <div className="text-3xl font-bold text-purple-600 mb-2">
              {allSectionsPassed ? '100' : Math.round(overallScore * 100)}%
            </div>
            <div className="text-sm font-medium text-gray-600">Overall Score</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryPage;